/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  Dropdown,
  DropdownList,
  DropdownUserAssistance,
  EmptyResults,
  LinkItem,
  preventDefault
} from '#PRIVATE_SelectCommon';
import type { DataState } from '#UNSAFE_Collection';
import { Separator } from '#UNSAFE_Separator';
import { InlineUserAssistance, type UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import type { ComponentChildren, ComponentProps, RefObject } from 'preact';
import type { CollectionRendererProps } from './SelectSingle.types';

type PickedDropdownProps = Pick<
  ComponentProps<typeof Dropdown>,
  'isOpen' | 'onAutoDismiss' | 'onPosition'
>;

type PickedInlineUserAssistanceProps = Pick<
  ComponentProps<typeof InlineUserAssistance>,
  'assistiveText' | 'helpSourceLink' | 'helpSourceText'
>;

type Props<K extends string | number, D extends Record<string, any>> = PickedDropdownProps &
  PickedInlineUserAssistanceProps & {
    data?: DataState<K, D> | null;
    collectionRenderer: (props: CollectionRendererProps<K, D>) => ComponentChildren;
    collectionRendererContext: CollectionRendererProps<K, D>;
    dropdownId: string;
    dropdownRef: RefObject<HTMLDivElement>;
    isAddToListShown?: boolean;
    isAdvancedSearchShown: boolean;
    isDisabled?: boolean;
    isDropdownAbove: boolean;
    isEmptyResults: boolean;
    isReadonly?: false;
    label: string;
    mainFieldRef: RefObject<HTMLDivElement>;
    onAddToListAction?: () => void;
    onAdvancedSearchAction?: () => void;
    userAssistanceDensity?: UserAssistanceDensityType;
  };

export function SelectSingleDesktopDropdown<
  K extends string | number,
  D extends Record<string, any>
>({
  assistiveText,
  collectionRenderer,
  collectionRendererContext,
  data,
  dropdownId,
  dropdownRef,
  helpSourceLink,
  helpSourceText,
  isAddToListShown,
  isAdvancedSearchShown,
  isDisabled,
  isDropdownAbove,
  isEmptyResults,
  isOpen,
  isReadonly,
  label,
  mainFieldRef,
  onAddToListAction,
  onAdvancedSearchAction,
  onAutoDismiss,
  onPosition,
  userAssistanceDensity
}: Props<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const dropdownInlineUserAssistance =
    isOpen &&
    !isDropdownAbove &&
    !isDisabled &&
    !isReadonly &&
    (assistiveText || helpSourceLink) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <TabbableModeContext.Provider value={{ isTabbable: false }}>
        <DropdownUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          userAssistanceDensity={userAssistanceDensity}
        />
      </TabbableModeContext.Provider>
    ) : undefined;

  const dropdownListContent = isEmptyResults ? (
    isAddToListShown || isAdvancedSearchShown ? undefined : (
      <EmptyResults />
    )
  ) : (
    collectionRenderer(collectionRendererContext)
  );

  const addToListContent = isAddToListShown ? (
    <>
      {!isEmptyResults && (
        <div onMouseDown={preventDefault}>
          <Separator />
        </div>
      )}
      <LinkItem isHighlighted={isEmptyResults} onAction={onAddToListAction}>
        {translations.select_addToList()}
      </LinkItem>
    </>
  ) : undefined;

  const advancedSearchContent = isAdvancedSearchShown ? (
    <>
      {!isEmptyResults && (
        <div onMouseDown={preventDefault}>
          <Separator />
        </div>
      )}
      <LinkItem isHighlighted={isEmptyResults} onAction={onAdvancedSearchAction}>
        {translations.select_moreSearchOptions()}
      </LinkItem>
    </>
  ) : undefined;

  return (
    <Dropdown
      anchorRef={mainFieldRef}
      dropdownRef={dropdownRef}
      id={dropdownId}
      isOpen={isOpen}
      onAutoDismiss={onAutoDismiss}
      onPosition={onPosition}>
      <DropdownList
        hasBottomGap={dropdownInlineUserAssistance === undefined}
        isLoading={data === null}>
        {dropdownListContent}
        {addToListContent}
        {advancedSearchContent}
      </DropdownList>
      {dropdownInlineUserAssistance}
    </Dropdown>
  );
}
